// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { useCallback, useState } from 'react';
import { botIdentifier } from '../../../lib/bot-identifier';
import { showErrorNotification } from '../../../utils/notifications';

export interface UseGrantSwePatients {
  grant: (patientIds: string[]) => Promise<void>;
  loading: boolean;
  error: string | undefined;
}

/**
 * Wraps `grant-swe-patient-access` bot execution.
 *
 * On success: shows a success toast and calls `onSuccess()` so the caller can
 * refresh / reload the patient list.
 */
export function useGrantSwePatients(sweId: string, onSuccess: () => void): UseGrantSwePatients {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const grant = useCallback(
    async (patientIds: string[]): Promise<void> => {
      if (patientIds.length === 0) {
        return;
      }
      setLoading(true);
      setError(undefined);
      try {
        await medplum.executeBot(botIdentifier('grant-swe-patient-access'), {
          sweId,
          patientIds,
        });
        notifications.show({
          color: 'green',
          title: 'Access granted',
          message: `Successfully granted access to ${patientIds.length} patient${patientIds.length === 1 ? '' : 's'}.`,
        });
        onSuccess();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        showErrorNotification(err);
      } finally {
        setLoading(false);
      }
    },
    [medplum, sweId, onSuccess]
  );

  return { grant, loading, error };
}
